export const automationsTranslations = {
  module: {
    title: 'Automations',
  },
  list: {
    title: 'Automations',
    search: 'Search automations...',
    searchAriaLabel: 'Search automations',
    noResults: 'No automations found',
    noResultsDescription: 'Get started by creating your first automation',
    noResultsFiltered: 'No automations match your filters',
    noResultsFilteredDescription: 'Try adjusting your search or filters',
    clearFilters: 'Clear Filters',
    createNew: 'Create Automation',
    allTriggers: 'All Triggers',
    all: 'All',
    ariaLabel: 'Automations list',
    errorLoading: (message: string) => `Error loading automations: ${message}`,
    filters: {
      status: 'Status',
      triggerType: 'Trigger Type',
      all: 'All',
    },
  },
  detail: {
    title: (name: string) => `Automation: ${name}`,
    created: 'Created',
    automationDetails: 'Automation Details',
    loading: 'Loading automation...',
    loadFailed: 'Failed to load automation',
    goBack: 'Go Back',
    newAutomation: 'New Automation',
    untitledAutomation: 'Untitled Automation',
    unsavedChanges: 'Unsaved changes',
    stay: 'Stay',
    leaveWithoutSaving: 'Leave Without Saving',
    tabs: {
      overview: 'Overview',
      flow: 'Flow',
      executions: 'History',
      versions: 'Versions',
      settings: 'Settings',
    },
    overview: {
      description: 'Description',
      noDescription: 'No description provided',
      status: 'Status',
      webhookEndpoint: 'Webhook Endpoint',
      webhookSecret: 'Secret',
      webhookLastUsed: 'Last used',
      created: 'Created',
      lastUpdated: 'Last Updated',
    },
    actions: {
      edit: 'Edit',
      activate: 'Activate',
      pause: 'Pause',
      delete: 'Delete',
      test: 'Test',
      duplicate: 'Duplicate',
      regenerateSecret: 'Regenerate Secret',
    },
    messages: {
      activated: 'Automation activated successfully',
      paused: 'Automation paused successfully',
      deleted: 'Automation deleted successfully',
      duplicated: 'Automation duplicated successfully',
      tested: 'Test run started successfully',
      created: 'Automation created successfully',
      saved: 'Automation saved successfully',
      activateFailed: (message: string) =>
        `Failed to activate automation: ${message}`,
      pauseFailed: (message: string) =>
        `Failed to pause automation: ${message}`,
      deleteFailed: (message: string) =>
        `Failed to delete automation: ${message}`,
      duplicateFailed: (message: string) =>
        `Failed to duplicate automation: ${message}`,
      testFailed: (message: string) => `Failed to start test run: ${message}`,
      createFailed: 'Failed to create automation. Please try again.',
      saveFailed: 'Failed to save automation. Please try again.',
    },
    statusLowercase: {
      active: 'activated',
      paused: 'paused',
      archived: 'archived',
    },
  },
  columns: {
    name: 'Name',
    status: 'Status',
    triggerType: 'Trigger Type',
    description: 'Description',
    createdAt: 'Created At',
    updatedAt: 'Updated At',
    updated: 'Updated',
  },
  dialogs: {
    delete: {
      title: 'Delete Automation',
      message: (name: string) =>
        `Are you sure you want to delete the automation "${name}"?`,
      warning:
        'This action cannot be undone. All execution history will be preserved, but the automation will no longer run.',
      confirm: 'Delete',
      cancel: 'Cancel',
    },
    deactivate: {
      title: 'Deactivate Automation',
      message: (name: string) =>
        `Are you sure you want to deactivate ${name}? Any running executions will complete, but no new executions will be triggered.`,
      confirm: 'Deactivate',
      cancel: 'Cancel',
    },
    discard: {
      title: 'Discard Changes?',
      message:
        'You have unsaved changes. Are you sure you want to discard them?',
      confirm: 'Discard',
      cancel: 'Cancel',
    },
    regenerateSecret: {
      title: 'Regenerate Webhook Secret',
      message:
        'Are you sure you want to regenerate the webhook secret? This will invalidate any existing integrations using the current secret.',
      confirm: 'Regenerate',
      cancel: 'Cancel',
      success: 'Webhook secret regenerated successfully',
      error: 'Failed to regenerate webhook secret',
    },
    test: {
      title: 'Test Automation',
      description: 'Configure the test payload for this automation run.',
      warning: 'Real Actions Will Execute',
      warningDescription:
        'This test will execute real actions - emails will be sent, HTTP requests will be made, and other side effects will occur. Use test data carefully.',
      triggerType: 'Trigger Type',
      runTest: 'Run Test',
      cancel: 'Cancel',
      unknown: 'Unknown',
      businessEvent: {
        eventType: 'Event Type',
        eventTypeDescription: 'Select the type of business event to simulate',
        eventTypeConfigured: 'Event type is configured in the trigger',
        data: 'Data',
        entity: 'Entity',
        entityDescription: 'Select the entity type',
        entityConfigured: 'Entity is configured in the trigger',
        event: 'Event',
        eventDescription: 'Select the event type',
        eventConfigured: 'Event is configured in the trigger',
        selectEntity: 'Select an entity to configure test data',
        dataTitle: (entity: string) => `${entity} Data`,
      },
      httpWebhook: {
        headers: 'Headers',
        headersDescription: 'HTTP headers to include in the test request',
        headerKeyPlaceholder: 'Header name',
        headerValuePlaceholder: 'Header value',
        queryParams: 'Query Parameters',
        queryParamsDescription: 'URL query parameters',
        queryKeyPlaceholder: 'Parameter name',
        queryValuePlaceholder: 'Parameter value',
        body: 'Request Body',
        bodyDescription: 'JSON body of the webhook request',
      },
      schedule: {
        scheduledTime: 'Scheduled Time',
        scheduledTimeDescription:
          'Specify the scheduled time that would trigger this automation. This simulates the schedule trigger firing at the specified time.',
        scheduledDate: 'Scheduled Date',
        time: 'Time',
      },
    },
    cancelExecution:
      'Are you sure you want to cancel this execution? This action cannot be undone.',
  },
  editor: {
    title: 'Flow Editor',
    create: {
      title: 'Create New Automation',
      selectTrigger: 'Select a trigger to start building your automation',
      namePlaceholder: 'Untitled Automation',
      descriptionPlaceholder: 'Add a description...',
      saveButton: 'Create Automation',
      triggerTypes: {
        businessEvent: {
          label: 'Business Event',
          description: 'Trigger when a specific business event occurs',
        },
        httpWebhook: {
          label: 'HTTP Webhook',
          description: 'Trigger via external HTTP POST request',
        },
        schedule: {
          label: 'Schedule',
          description: 'Trigger on a recurring schedule',
        },
      },
    },
    palette: {
      title: 'Integrations',
      search: 'Search integrations...',
      categories: {
        triggers: 'Triggers',
        actions: 'Actions',
        conditions: 'Conditions',
        notifications: 'Notifications',
        http: 'HTTP',
        userAction: 'User Action',
        flowControl: 'Flow Control',
      },
      noNodes: 'No nodes available',
    },
    canvas: {
      addNode: 'Drag and drop an integration from the palette',
      deleteNode: 'Delete node',
      deleteEdge: 'Delete connection',
    },
    config: {
      title: 'Node Configuration',
      save: 'Save',
      cancel: 'Cancel',
      close: 'Close',
      unknownNodeType: (type: string) => `Unknown node type: ${type}`,
      fields: {
        label: 'Label',
        labelPlaceholder: 'Enter node label...',
        description: 'Description',
        descriptionPlaceholder: 'Enter description...',
        type: 'Type',
      },
    },
    details: {
      title: 'Automation Details',
      name: 'Name',
      namePlaceholder: 'Enter automation name...',
      description: 'Description',
      descriptionPlaceholder: 'Enter description...',
      triggerType: 'Trigger Type',
      status: 'Status',
      webhookEndpoint: 'Webhook Endpoint',
      url: 'URL',
      secret: 'Secret',
      lastUsed: 'Last Used',
      never: 'Never',
      copyUrl: 'Copy URL',
      copySecret: 'Copy secret',
      showSecret: 'Show secret',
      hideSecret: 'Hide secret',
      webhookGenerateHint:
        'Webhook URL and secret will be generated when you save this automation.',
    },
    triggers: {
      httpWebhook: {
        title: 'HTTP Webhook Trigger',
        description:
          'A secure webhook URL and signing secret will be automatically generated when you save this automation.',
        urlGenerated: 'Unique webhook URL will be generated',
        signatureEnabled: 'HMAC-SHA256 signature verification enabled',
        afterSaving:
          "After saving: You'll find the webhook URL and secret in the automation details page. Use the secret to sign your webhook payloads with HMAC-SHA256 and include the signature in the",
        signatureHeader: 'X-Webhook-Signature',
        header: 'header.',
      },
    },
    actions: {
      save: 'Save Flow',
      cancel: 'Cancel',
      undo: 'Undo',
      redo: 'Redo',
      cut: 'Cut',
      copy: 'Copy',
      paste: 'Paste',
      layout: 'Layout',
      autoLayout: 'Auto Layout',
      vertical: 'Vertical',
      horizontal: 'Horizontal',
      zoomIn: 'Zoom In',
      zoomOut: 'Zoom Out',
      fitView: 'Fit View',
      add: 'Add',
      remove: 'Remove',
    },
    messages: {
      saved: 'Flow saved successfully',
      unsavedChanges:
        'You have unsaved changes. Are you sure you want to leave?',
    },
  },
  executions: {
    title: 'Execution History',
    noResults: 'No execution runs yet',
    noResultsDescription:
      'Execution history will appear here once the automation runs',
    total: (count: number) =>
      `${count} execution run${count !== 1 ? 's' : ''} total`,
    errorLoading: (message: string) =>
      `Error loading execution runs: ${message}`,
    triggered: 'Triggered',
    duration: 'Duration',
    nodes: (count: number) => `${count} nodes`,
    actions: (count: number) => `${count} actions`,
    executionTime: 'Execution time',
    cancel: 'Cancel',
    test: 'Test',
    columns: {
      id: 'ID',
      status: 'Status',
      startedAt: 'Started',
      completedAt: 'Completed',
      duration: 'Duration',
      error: 'Error',
    },
    viewDetails: 'View Details',
  },
  executionDetail: {
    title: 'Execution Run',
    back: 'Back to Automation',
    triggeredAt: 'Triggered At',
    completedAt: 'Completed At',
    duration: 'Duration',
    nodesExecuted: 'Nodes Executed',
    triggerPayload: 'Trigger Payload',
    nodeTrace: 'Node Execution Trace',
    noNodes: 'No node executions recorded',
    test: 'Test',
    nodeHeaders: {
      node: 'Node',
      type: 'Type',
      status: 'Status',
      duration: 'Duration',
      input: 'Input',
      output: 'Output',
      error: 'Error',
    },
    nodeStatus: {
      Success: 'Success',
      Failed: 'Failed',
      Skipped: 'Skipped',
    },
  },
  form: {
    name: 'Name',
    description: 'Description',
    triggerType: 'Trigger Type',
    status: 'Status',
    enabled: 'Enabled',
    validation: {
      nameRequired: 'Name is required',
      triggerTypeRequired: 'Trigger type is required',
    },
  },
  status: {
    Draft: 'Draft',
    Active: 'Active',
    Paused: 'Paused',
    Archived: 'Archived',
  },
  triggerType: {
    BusinessEvent: 'Business Event',
    HttpWebhook: 'HTTP Webhook',
    Schedule: 'Schedule',
  },
  executionStatus: {
    Pending: 'Pending',
    Running: 'Running',
    WaitingApproval: 'Waiting Approval',
    Completed: 'Completed',
    Failed: 'Failed',
    Cancelled: 'Cancelled',
  },
  versionHistory: {
    title: 'Version History',
    versionLabel: (n: number) => `Version ${n}`,
    versionCount: 'version',
    versionCountPlural: 'versions',
    createdBy: 'by',
    unknown: 'Unknown',
    noVersions: 'No versions yet',
    noVersionsDescription:
      'Version history will appear here once the automation is saved.',
    flowSnapshot: 'Flow Snapshot',
    actions: {
      view: 'View',
      restore: 'Restore',
    },
    restoreDialog: {
      title: 'Restore Version',
      message: (n: number) =>
        `This will replace the current flow with Version ${n}. Any unsaved changes will be lost.`,
      confirm: 'Restore',
      cancel: 'Cancel',
    },
    messages: {
      restoreSuccess: 'Version restored successfully',
      restoreError: 'Failed to restore version',
    },
  },
  bulk: {
    selected: 'automation selected',
    selectedPlural: 'automations selected',
    actions: {
      activate: 'Activate',
      pause: 'Pause',
      delete: 'Delete',
    },
    messages: {
      activateSuccess: '{count} automation(s) activated successfully',
      activatePartial: '{success} activated, {failed} failed',
      activateError: 'Failed to activate automations',
      pauseSuccess: '{count} automation(s) paused successfully',
      pausePartial: '{success} paused, {failed} failed',
      pauseError: 'Failed to pause automations',
      deleteSuccess: '{count} automation(s) deleted successfully',
      deletePartial: '{success} deleted, {failed} failed',
      deleteError: 'Failed to delete automations',
    },
    deleteDialog: {
      title: 'Delete Automations',
      message:
        'Are you sure you want to delete {count} automation(s)? This action cannot be undone.',
      warning:
        'All execution history will be preserved, but the automations will no longer run.',
      confirm: 'Delete',
      cancel: 'Cancel',
    },
  },
  approvals: {
    title: 'Approval Request',
    approvalStep: 'Approval Step',
    automation: 'Automation',
    status: 'Status',
    instructions: 'Instructions',
    requestedAt: 'Requested At',
    expiresAt: 'Expires At',
    respondedAt: 'Responded At',
    respondedBy: 'Responded By',
    responseComment: 'Response Comment',
    executionContext: 'Execution Context',
    addComment: 'Add a comment (optional)',
    commentPlaceholder: 'Add any notes or reasoning for this decision...',
    review: 'Review',
    approve: 'Approve',
    reject: 'Reject',
    cancel: 'Cancel',
    close: 'Close',
    loading: 'Loading approval details...',
    loadFailed: 'Failed to load approval details',
    respondFailed: 'Failed to respond to approval request',
    approvedSuccess: 'Approval request approved',
    rejectedSuccess: 'Approval request rejected',
    noApprovals: 'No approval requests',
    noApprovalsDescription: 'There are no pending approvals at the moment',
    noApprovalsFiltered: 'No approvals match your filters',
    noApprovalsFilteredDescription: 'Try adjusting your search or filters',
    errorLoading: (message: string) => `Error loading approvals: ${message}`,
    ariaLabel: 'Approval requests list',
    requestExpired: 'Request Expired',
    requestExpiredDescription:
      'This approval request has expired and can no longer be approved or rejected.',
    alreadyResponded: 'Already Responded',
    alreadyRespondedDescription: (status: string) =>
      `This approval request has already been ${status}.`,
    notAvailable: 'N/A',
    statusLabels: {
      Pending: 'Pending',
      Approved: 'Approved',
      Rejected: 'Rejected',
      Expired: 'Expired',
    },
  },
  testForm: {
    bookingId: 'Booking ID',
    bookingNumber: 'Booking Number',
    vesselName: 'Vessel Name',
    steamshipLine: 'Steamship Line',
    loadingPortId: 'Loading Port ID',
    destinationPortId: 'Destination Port ID',
    purchaseOrderId: 'Purchase Order ID',
    loadId: 'Load ID',
    documentNumber: 'Document Number',
    containerNumber: 'Container Number',
    sealNumber: 'Seal Number',
    heavyWeight: 'Heavy Weight (lbs)',
    lightWeight: 'Light Weight (lbs)',
    salesOrderId: 'Sales Order ID',
    bookingIdRef: 'Booking ID',
    facilityId: 'Facility ID',
    accountId: 'Account ID',
    paymentCurrency: 'Payment Currency',
    notes: 'Notes',
    itemId: 'Item ID',
    skuNumber: 'SKU Number',
    itemName: 'Name',
    categoryId: 'Category ID',
    itemGroupId: 'Item Group ID',
    itemDescription: 'Description',
    invoiceId: 'Invoice ID',
    invoiceAmount: 'Amount',
    dueDate: 'Due Date',
  },
};

export type AutomationsTranslations = typeof automationsTranslations;
